import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cctv, Search, RefreshCw, ImageOff, ChevronUp, ChevronDown, Video, X, Wifi, WifiOff, Landmark, GitMerge, Pencil, Check } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import baseapi, { API_BASE, useApiOutage } from '../baseapi';
import CameraStreamModal from '../components/junctions/CameraStreamModal';

/** One row of GET /api/cameras/overview */
interface CameraRow {
  id: string;
  name: string;
  checkpoint: string | null;
  checkpoint_name: string | null;
  group: string | null;
  group_name: string | null;
  ip: string | null;
  lat?: number | null;
  lng?: number | null;
  type: string | null;
  pole: string | null;
  ps: string | null;
  server: 'local' | 'remote';
  state: string;
  online: boolean;
  /** false = view only: not counted, the stream opens when somebody looks at the camera */
  analytics?: boolean;
  /** view-only camera that is closed: true = it answers on the network and can be opened */
  reachable?: boolean | null;
  detail: string;
  frame_age_s: number | null;
  proc_fps: number | null;
  frame_size: [number, number] | null;
  tracks: number | null;
  lines: number;
  state_since: number | null;
  last_seen: number | null;
  snapshot_ts: number | null;
  snapshot_url: string | null;
  today: { people_toward: number; people_away: number; vehicles: number; last_crossing_ts: number | null };
}

interface Overview {
  server_time: number;
  summary: { total: number; online: number; offline: number; by_state: Record<string, number>; local: number; remote: number };
  /** every area of the config (also those without a camera), for moving a camera to another area */
  groups?: { id: string; name: string }[];
  cameras: CameraRow[];
}

type SortKey = 'status' | 'name' | 'location' | 'toward' | 'away';
type StatusFilter = 'all' | 'online' | 'offline';

const REFRESH_MS = 10000;

const STATE_STYLE: Record<string, { label: string; cls: string; dot: string }> = {
  online: { label: 'Online', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/25', dot: 'bg-emerald-500' },
  replay: { label: 'Replay', cls: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/25', dot: 'bg-sky-500' },
  stale: { label: 'Stale', cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/25', dot: 'bg-amber-500' },
  connecting: { label: 'Connecting', cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/25', dot: 'bg-amber-500' },
  unavailable: { label: 'Offline', cls: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/25', dot: 'bg-rose-500' },
  standby: { label: 'Online · on demand', cls: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/25', dot: 'bg-sky-400' },
  no_camera: { label: 'No camera', cls: 'bg-neutral-100 text-neutral-600 border-neutral-200 dark:bg-white/5 dark:text-neutral-400 dark:border-white/10', dot: 'bg-neutral-400' },
};
const stateStyle = (s: string) => STATE_STYLE[s] ?? { label: s || 'Unknown', cls: STATE_STYLE.no_camera.cls, dot: 'bg-neutral-400' };

const fmtClock = (ts: number | null) =>
  ts ? new Date(ts * 1000).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : '—';

const fmtAgo = (ts: number | null, now: number) => {
  if (!ts) return '—';
  const s = Math.max(0, Math.round(now - ts));
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h ago`;
};

const snapshotSrc = (c: CameraRow, thumb: boolean) =>
  c.snapshot_url ? `${API_BASE}${c.snapshot_url}?${thumb ? 'thumb=1&' : ''}t=${Math.floor(c.snapshot_ts ?? 0)}` : null;

export const CamerasPage: React.FC = () => {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message
  const opened = (useLocation().state as { status?: StatusFilter } | null)?.status;   // e.g. Overview's camera row opens the offline list
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(opened ?? 'all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'status', dir: 1 });
  const [preview, setPreview] = useState<CameraRow | null>(null);
  const [liveCheckpoint, setLiveCheckpoint] = useState<any | null>(null);
  const [liveCameraId, setLiveCameraId] = useState<string | undefined>();
  const [liveError, setLiveError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  // location being edited in place: camera id + the text in the three boxes
  const [edit, setEdit] = useState<{ id: string; name: string; group: string; lat: string; lng: string; busy: boolean; err: string | null } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await baseapi.get('cameras/overview');
      if (!res.data || !Array.isArray(res.data.cameras)) throw new Error('unavailable');
      setData(res.data as Overview);
      setError(null);
    } catch (e: any) {
      const notHere = e?.response?.status === 404 || e?.message === 'unavailable';
      setError(notHere ? 'The camera list is not available on this server.' : 'Could not load the camera list — retrying.');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Save the edited location name and map position (PUT cameras/{id}/details); kept in the server's config. */
  const saveEdit = async () => {
    if (!edit) return;
    const name = edit.name.trim();
    const lat = edit.lat.trim(), lng = edit.lng.trim();
    if (!name) { setEdit({ ...edit, err: 'Give the camera a name.' }); return; }
    if ((lat === '') !== (lng === '')) { setEdit({ ...edit, err: 'Give both latitude and longitude, or leave both empty.' }); return; }
    const la = Number(lat), lo = Number(lng);
    if (lat !== '' && (!Number.isFinite(la) || !Number.isFinite(lo) || Math.abs(la) > 90 || Math.abs(lo) > 180)) {
      setEdit({ ...edit, err: 'Latitude must be -90 to 90 and longitude -180 to 180.' }); return;
    }
    setEdit({ ...edit, busy: true, err: null });
    try {
      const was = data?.cameras.find(x => x.id === edit.id);
      const group = edit.group && edit.group !== (was?.group ?? '') ? edit.group : undefined;
      const body = { name, ...(group ? { group } : {}), ...(lat === '' ? { clear_position: true } : { lat: la, lng: lo }) };
      await baseapi.put(`cameras/${encodeURIComponent(edit.id)}/details`, body);
      const groupName = group ? (data?.groups ?? []).find(g => g.id === group)?.name ?? group : undefined;
      setData(d => d && { ...d, cameras: d.cameras.map(x => (x.id === edit.id
        ? { ...x, name, lat: lat === '' ? null : la, lng: lat === '' ? null : lo, ...(group ? { group, group_name: groupName ?? null } : {}) } : x)) });
      setEdit(null);
      window.setTimeout(load, 3500);
    } catch (e: any) {
      const shared = e?.response?.status === 409;
      setEdit(ed => ed && { ...ed, busy: false, err: shared
        ? 'This camera shares its counting point with other cameras, so its area cannot be changed here. The name was not changed either.'
        : 'Not saved. Check the connection and try again.' });
    }
  };

  /** Analysed (always decoded and counted) or view only (no counting; the stream opens on demand). */
  const setAnalytics = async (c: CameraRow, enabled: boolean) => {
    setSwitching(c.id);
    setLiveError(null);
    try {
      await baseapi.put(`cameras/${encodeURIComponent(c.id)}/analytics`, { enabled });
      setData(d => d && { ...d, cameras: d.cameras.map(x => (x.id === c.id ? { ...x, analytics: enabled } : x)) });
      window.setTimeout(load, 1500);
    } catch {
      setLiveError(`Could not switch analytics for ${c.name}. Nothing was changed.`);
    } finally {
      setSwitching(null);
    }
  };

  useEffect(() => {
    load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const now = data?.server_time ?? Date.now() / 1000;

  // Top widgets, all derived from the same list the table shows
  const stats = useMemo(() => {
    const cams = data?.cameras ?? [];
    const part = (server: 'local' | 'remote') => {
      const list = cams.filter(c => c.server === server);
      return { total: list.length, online: list.filter(c => c.online || c.state === 'standby').length };
    };
    const online = cams.filter(c => c.online).length;
    // a view-only camera that nobody is watching is closed on purpose: it is not "offline"
    const onDemand = cams.filter(c => c.state === 'standby').length;
    return {
      total: cams.length,
      online: online + onDemand,        // a closed view-only camera that answers on the network is online
      offline: cams.length - online - onDemand,
      onDemand,
      temple: part('local'),
      junction: part('remote'),
    };
  }, [data]);

  const groups = useMemo(() => {
    const m = new Map<string, string>();
    (data?.cameras ?? []).forEach(c => { if (c.group) m.set(c.group, c.group_name || c.group); });
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (data?.cameras ?? []).filter(c => {
      if (statusFilter === 'online' && !c.online && c.state !== 'standby') return false;
      if (statusFilter === 'offline' && (c.online || c.state === 'standby')) return false;
      if (statusFilter === 'online' && c.state === 'standby') return true;
      if (groupFilter !== 'all' && c.group !== groupFilter) return false;
      if (!q) return true;
      return [c.id, c.name, c.checkpoint_name, c.group_name, c.ip, c.ps, c.pole, c.type]
        .some(v => (v ?? '').toString().toLowerCase().includes(q));
    });
    const val = (c: CameraRow): string | number => {
      switch (sort.key) {
        case 'status': return c.online ? 1 : 0;                 // offline cameras first
        case 'name': return c.name.toLowerCase();
        case 'location': return `${c.group_name ?? ''} ${c.checkpoint_name ?? ''}`.toLowerCase();
        case 'toward': return c.today.people_toward;
        case 'away': return c.today.people_away;
      }
    };
    return [...list].sort((a, b) => {
      // cameras that are counted come first, view-only ones after them; the chosen sorting applies inside each part
      const an = Number(b.analytics !== false) - Number(a.analytics !== false);
      if (an) return an;
      const x = val(a), y = val(b);
      const cmp = x < y ? -1 : x > y ? 1 : a.id.localeCompare(b.id, undefined, { numeric: true });
      return x === y ? cmp : cmp * sort.dir;
    });
  }, [data, query, statusFilter, groupFilter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort(s => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

  // Live view: reuse the junction camera popup with this camera's checkpoint from /api/summary
  const openLive = async (c: CameraRow) => {
    setLiveError(null);
    try {
      const res = await baseapi.get('summary');
      const cp = c.checkpoint ? res.data?.checkpoints?.[c.checkpoint] : null;
      if (!cp) throw new Error('no checkpoint');
      setLiveCameraId(c.id);
      setLiveCheckpoint(cp);
    } catch {
      setLiveError(`Live view for ${c.id} could not be opened.`);
    }
  };

  const SortHead: React.FC<{ k: SortKey; children: React.ReactNode; className?: string }> = ({ k, children, className }) => (
    <th className={`px-3 py-2.5 font-semibold ${className ?? ''}`}>
      <button onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 uppercase tracking-wide whitespace-nowrap cursor-pointer hover:text-neutral-900 dark:hover:text-white">
        {children}
        {sort.key === k ? (sort.dir === 1 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null}
      </button>
    </th>
  );

  const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);
  const pick = (f: StatusFilter) => setStatusFilter(cur => (cur === f ? 'all' : f));
  const card = 'rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs';

  return (
    <div className="space-y-4">
      {/* Header + summary */}
      <div className={`${card} p-3.5 flex flex-col lg:flex-row lg:items-center justify-between gap-3`}>
        <div>
          <h1 className="text-base font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
            <Cctv className="w-5 h-5 text-blue-500" />
            Camera Status
          </h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Every monitored camera with its current state, last snapshot and today's counts · refreshes every {REFRESH_MS / 1000}s
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button
            onClick={() => { setLoading(true); load(); }}
            className="p-2 rounded-lg border border-neutral-200 dark:border-[#2b3140] bg-neutral-50 dark:bg-[#1c202a] text-neutral-600 dark:text-neutral-300 hover:text-blue-500 cursor-pointer"
            title="Refresh now" aria-label="Refresh now"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Widgets */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {[
          { key: 'total', label: 'Total cameras', value: stats.total, sub: `${stats.temple.total} temple · ${stats.junction.total} junction`,
            Icon: Cctv, tone: 'text-blue-600 dark:text-blue-400', bar: null as number | null, barCls: '', filter: 'all' as StatusFilter },
          { key: 'online', label: 'Online', value: stats.online, sub: `${pct(stats.online, stats.total)}% of all cameras`,
            Icon: Wifi, tone: 'text-emerald-600 dark:text-emerald-400', bar: pct(stats.online, stats.total), barCls: 'bg-emerald-500', filter: 'online' as StatusFilter },
          { key: 'offline', label: 'Offline', value: stats.offline, sub: stats.offline ? 'Need attention' : 'None offline',
            Icon: WifiOff, tone: 'text-rose-600 dark:text-rose-400', bar: pct(stats.offline, stats.total), barCls: 'bg-rose-500', filter: 'offline' as StatusFilter },
          { key: 'temple', label: 'Temple cameras', value: `${stats.temple.online}/${stats.temple.total}`, sub: `${pct(stats.temple.online, stats.temple.total)}% online`,
            Icon: Landmark, tone: 'text-teal-600 dark:text-teal-400', bar: pct(stats.temple.online, stats.temple.total), barCls: 'bg-teal-500', filter: null },
          { key: 'junction', label: 'Junction cameras', value: `${stats.junction.online}/${stats.junction.total}`, sub: `${pct(stats.junction.online, stats.junction.total)}% online`,
            Icon: GitMerge, tone: 'text-violet-600 dark:text-violet-400', bar: pct(stats.junction.online, stats.junction.total), barCls: 'bg-violet-500', filter: null },
        ].map(w => {
          const active = w.filter !== null && w.filter !== 'all' && statusFilter === w.filter;
          const body = (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] uppercase tracking-wide font-semibold text-neutral-500 dark:text-neutral-400">{w.label}</span>
                <w.Icon className={`w-4 h-4 ${w.tone}`} />
              </div>
              <div className={`mt-1 text-2xl font-bold tabular-nums leading-tight ${w.tone}`}>{data ? w.value : '—'}</div>
              <div className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">{data ? w.sub : ' '}</div>
              <div className="mt-2 h-1 rounded-full bg-neutral-100 dark:bg-white/5 overflow-hidden">
                {w.bar !== null && <div className={`h-full rounded-full ${w.barCls}`} style={{ width: `${data ? w.bar : 0}%` }} />}
              </div>
            </>
          );
          const cls = `${card} p-3 text-left ${active ? 'ring-2 ring-blue-500/60 border-blue-400 dark:border-blue-500/60' : ''}`;
          return w.filter !== null ? (
            <button key={w.key} onClick={() => (w.filter === 'all' ? setStatusFilter('all') : pick(w.filter as StatusFilter))}
              className={`${cls} cursor-pointer hover:border-blue-300 dark:hover:border-blue-500/40`} aria-pressed={active}
              title={w.filter === 'all' ? 'Show all cameras' : `Show only: ${w.label.toLowerCase()}`}>
              {body}
            </button>
          ) : (
            <div key={w.key} className={cls}>{body}</div>
          );
        })}
      </div>

      {/* Filters */}
      <div className={`${card} p-3 flex flex-col md:flex-row md:items-center gap-2.5`}>
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search camera, location or IP…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-neutral-200 dark:border-[#2b3140] bg-white dark:bg-[#12151c] text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex items-center rounded-lg border border-neutral-200 dark:border-[#2b3140] bg-neutral-100 dark:bg-[#1c202a] p-0.5 text-xs font-semibold">
          {(['all', 'online', 'offline'] as StatusFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 rounded-md capitalize cursor-pointer ${
                statusFilter === f ? 'bg-white dark:bg-[#282e3d] text-blue-600 dark:text-blue-400 shadow-xs' : 'text-neutral-500 dark:text-neutral-400'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <select
          value={groupFilter}
          onChange={e => setGroupFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-neutral-200 dark:border-[#2b3140] bg-white dark:bg-[#12151c] text-neutral-900 dark:text-neutral-100 focus:outline-none focus:border-blue-500"
          aria-label="Filter by area"
        >
          <option value="all">All areas</option>
          {groups.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>

      {(error || liveError) && (outage || /not available/.test(error || '')) && (
        <div className="px-3.5 py-2.5 rounded-lg text-sm font-medium border bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/25">
          {error || liveError}
        </div>
      )}

      {/* Table */}
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm min-w-[1060px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-[#262c39]">
              <th className="px-3 py-2.5 font-semibold w-[132px] whitespace-nowrap">Last snapshot</th>
              <SortHead k="name">Camera</SortHead>
              <SortHead k="location">Location</SortHead>
              <SortHead k="status">Status</SortHead>
              <th className="px-3 py-2.5 font-semibold">Stream</th>
              <th className="px-3 py-2.5 font-semibold" title="On: always decoded and counted. Off: view only, the stream opens when somebody looks at the camera.">Analytics</th>
              <SortHead k="toward" className="text-right">Toward</SortHead>
              <SortHead k="away" className="text-right">Away</SortHead>
              <th className="px-3 py-2.5 font-semibold whitespace-nowrap">Last activity</th>
              <th className="px-3 py-2.5 font-semibold text-right">Live</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => {
              const st = stateStyle(c.state);
              const thumb = snapshotSrc(c, true);
              return (
                <tr key={c.id} className="border-b last:border-b-0 border-neutral-100 dark:border-[#20252f] hover:bg-neutral-50 dark:hover:bg-white/[0.03] align-middle">
                  <td className="px-3 py-2">
                    {thumb ? (
                      <button onClick={() => setPreview(c)} className="block cursor-pointer" title={`Snapshot taken ${fmtClock(c.snapshot_ts)}`}>
                        <img
                          src={thumb}
                          alt={`Last snapshot of ${c.name}`}
                          loading="lazy"
                          className={`w-[112px] h-[63px] object-cover rounded-md border border-neutral-200 dark:border-[#2b3140] bg-black ${c.online || c.state === 'standby' ? '' : 'opacity-70 grayscale'}`}
                        />
                        <span className="block mt-0.5 text-[10px] text-neutral-500 dark:text-neutral-400 tabular-nums">{fmtAgo(c.snapshot_ts, now)}</span>
                      </button>
                    ) : (
                      <div className="w-[112px] h-[63px] rounded-md border border-dashed border-neutral-300 dark:border-[#2b3140] flex flex-col items-center justify-center text-neutral-400 text-[10px] gap-0.5">
                        <ImageOff className="w-4 h-4" />
                        No snapshot yet
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {edit?.id === c.id ? (
                      <form className="flex flex-col gap-1 min-w-[230px]" onSubmit={e => { e.preventDefault(); saveEdit(); }}>
                        <input autoFocus value={edit.name} maxLength={120} aria-label="Camera name" placeholder="Camera name"
                          onChange={e => setEdit({ ...edit, name: e.target.value, err: null })}
                          onKeyDown={e => { if (e.key === 'Escape') setEdit(null); }}
                          className="px-2 py-1 rounded-md border border-blue-300 dark:border-blue-500/40 bg-white dark:bg-[#12151c] text-sm font-semibold text-neutral-900 dark:text-neutral-100 outline-none focus:ring-2 focus:ring-blue-400/40" />
                        <select value={edit.group} aria-label="Location (area)" onChange={e => setEdit({ ...edit, group: e.target.value, err: null })}
                          className="px-2 py-1 rounded-md border border-neutral-300 dark:border-[#2b3140] bg-white dark:bg-[#12151c] text-xs text-neutral-800 dark:text-neutral-200 outline-none focus:ring-2 focus:ring-blue-400/40 cursor-pointer">
                          {!edit.group && <option value="">No area</option>}
                          {(data?.groups ?? groups.map(([id, name]) => ({ id, name }))).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                        <div className="flex gap-1">
                          <input value={edit.lat} inputMode="decimal" aria-label="Latitude" placeholder="Latitude"
                            onChange={e => setEdit({ ...edit, lat: e.target.value, err: null })}
                            className="w-0 flex-1 px-2 py-1 rounded-md border border-neutral-300 dark:border-[#2b3140] bg-white dark:bg-[#12151c] text-xs tabular-nums text-neutral-800 dark:text-neutral-200 outline-none focus:ring-2 focus:ring-blue-400/40" />
                          <input value={edit.lng} inputMode="decimal" aria-label="Longitude" placeholder="Longitude"
                            onChange={e => setEdit({ ...edit, lng: e.target.value, err: null })}
                            className="w-0 flex-1 px-2 py-1 rounded-md border border-neutral-300 dark:border-[#2b3140] bg-white dark:bg-[#12151c] text-xs tabular-nums text-neutral-800 dark:text-neutral-200 outline-none focus:ring-2 focus:ring-blue-400/40" />
                          <button type="submit" disabled={edit.busy} title="Save" aria-label="Save location"
                            className="px-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 cursor-pointer"><Check className="w-3.5 h-3.5" /></button>
                          <button type="button" onClick={() => setEdit(null)} title="Cancel" aria-label="Cancel"
                            className="px-2 rounded-md border border-neutral-300 dark:border-[#2b3140] text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-white/5 cursor-pointer"><X className="w-3.5 h-3.5" /></button>
                        </div>
                        {edit.err && <div className="text-[11px] text-rose-600 dark:text-rose-400">{edit.err}</div>}
                      </form>
                    ) : (
                      <div className="group/name flex items-start gap-1.5">
                        <div className="font-semibold text-neutral-900 dark:text-neutral-100 leading-snug">{c.name}</div>
                        <button type="button" title="Change camera name, location and map position" aria-label={`Edit ${c.name}`}
                          onClick={() => setEdit({ id: c.id, name: c.name, group: c.group ?? '', lat: c.lat != null ? String(c.lat) : '', lng: c.lng != null ? String(c.lng) : '', busy: false, err: null })}
                          className="mt-0.5 p-0.5 rounded text-neutral-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 opacity-60 group-hover/name:opacity-100 focus:opacity-100 cursor-pointer">
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <div className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono">
                      {c.id}{c.ip ? ` · ${c.ip}` : ''}{c.type ? ` · ${c.type}` : ''}
                    </div>
                    {c.lat != null && c.lng != null && edit?.id !== c.id && (
                      <div className="text-[10px] text-neutral-400 tabular-nums">{c.lat.toFixed(6)}, {c.lng.toFixed(6)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-neutral-800 dark:text-neutral-200 leading-snug">{c.group_name || '—'}</div>
                    <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
                      {[c.ps, c.pole ? `Pole ${c.pole}` : null].filter(Boolean).join(' · ') || c.checkpoint || ''}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-bold ${st.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                      {st.label}
                    </span>
                    {!c.online && c.detail && (
                      <div className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400 max-w-[240px] truncate" title={c.detail}>{c.detail}</div>
                    )}
                    {c.state_since && (
                      <div className="mt-0.5 text-[10px] text-neutral-400 tabular-nums">since {fmtClock(c.state_since)}</div>
                    )}
                    {!c.online && c.last_seen && (
                      <div className="mt-0.5 text-[10px] text-neutral-400 tabular-nums" title={fmtClock(c.last_seen)}>last seen {fmtAgo(c.last_seen, now)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-neutral-700 dark:text-neutral-300">
                    {c.online ? (
                      <>
                        <div>{(c.proc_fps ?? 0).toFixed(1)} fps</div>
                        <div className="text-[10px] text-neutral-400">
                          {c.frame_size && c.frame_size[0] ? `${c.frame_size[0]}×${c.frame_size[1]}` : ''}
                        </div>
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={c.analytics !== false}
                      aria-label={`Analytics for ${c.name}`}
                      disabled={switching === c.id}
                      onClick={() => setAnalytics(c, c.analytics === false)}
                      title={c.analytics !== false ? 'Counted all the time. Click to make it view only.' : 'View only: not counted. Click to start counting.'}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${c.analytics !== false ? 'bg-emerald-500' : 'bg-neutral-300 dark:bg-neutral-600'}`}
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${c.analytics !== false ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                    </button>
                    <div className="mt-0.5 text-[10px] text-neutral-400">{c.analytics !== false ? 'Counting' : 'View only'}</div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-orange-600 dark:text-orange-400">{c.today.people_toward.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-sky-600 dark:text-sky-400">{c.today.people_away.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 tabular-nums text-neutral-600 dark:text-neutral-300">{fmtAgo(c.today.last_crossing_ts, now)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => openLive(c)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold border border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100 dark:border-blue-500/25 dark:text-blue-400 dark:bg-blue-500/10 dark:hover:bg-blue-500/20 cursor-pointer"
                    >
                      <Video className="w-3.5 h-3.5" />
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
                  {loading ? 'Loading cameras…' : data ? 'No cameras match these filters.' : 'No camera data.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {data && (
        <div className="text-[11px] text-neutral-500 dark:text-neutral-400 px-1">
          Showing {rows.length} of {data.cameras.length} cameras · counts are pedestrians since the start of today's reporting day · updated {fmtClock(data.server_time)}
        </div>
      )}

      {/* Snapshot preview */}
      {preview && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="max-w-5xl w-full rounded-xl overflow-hidden bg-white dark:bg-[#14171f] border border-neutral-200 dark:border-[#262c39] shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-neutral-200 dark:border-[#262c39]">
              <div className="min-w-0">
                <div className="font-bold text-sm text-neutral-900 dark:text-white truncate">{preview.name}</div>
                <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
                  {preview.id} · last snapshot {fmtClock(preview.snapshot_ts)} ({fmtAgo(preview.snapshot_ts, now)})
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-900 dark:hover:text-white cursor-pointer" aria-label="Close snapshot">
                <X className="w-4 h-4" />
              </button>
            </div>
            <img src={snapshotSrc(preview, false) ?? ''} alt={`Last snapshot of ${preview.name}`} className="w-full max-h-[78vh] object-contain bg-black" />
          </div>
        </div>
      )}

      {/* Live view popup (same one the Junctions page uses) */}
      {liveCheckpoint && (
        <CameraStreamModal
          checkpoint={liveCheckpoint}
          initialCameraId={liveCameraId}
          onClose={() => { setLiveCheckpoint(null); setLiveCameraId(undefined); }}
        />
      )}
    </div>
  );
};
